import striptags from 'striptags'

import type { OutputData } from '@editorjs/editorjs'

export interface FaqItem {
    question: string
    answer: string
}

const FAQ_HEADING =
    /^(частые вопросы|часто задаваемые вопросы|вопросы и ответы|faq|ответы на вопросы)$/i

const text = (html: string): string =>
    striptags(html ?? '')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()

/**
 * FAQ статьи для FAQPage JSON-LD: раздел H2 «Частые вопросы», внутри — H3-вопрос и абзацы/списки-ответ.
 * Так пишет SEO-агент, так же можно оформить и ручную статью — отдельный блок в редакторе не нужен.
 */
export function extractFaq(data: OutputData | null | undefined): FaqItem[] {
    const blocks = data?.blocks ?? []
    const start = blocks.findIndex(
        b =>
            b.type === 'header' &&
            b.data?.level === 2 &&
            FAQ_HEADING.test(text(b.data.text))
    )
    if (start < 0) return []

    const items: FaqItem[] = []
    let current: { question: string; parts: string[] } | null = null
    const flush = (): void => {
        if (current && current.parts.length) {
            items.push({
                question: current.question,
                answer: current.parts.join(' '),
            })
        }
    }

    for (const block of blocks.slice(start + 1)) {
        if (block.type === 'header' && block.data?.level <= 2) break
        if (block.type === 'header' && block.data?.level === 3) {
            flush()
            current = { question: text(block.data.text), parts: [] }
        } else if (current && block.type === 'paragraph') {
            current.parts.push(text(block.data.text))
        } else if (current && block.type === 'list') {
            current.parts.push(
                (block.data.items ?? [])
                    .map((i: { content?: string } | string) =>
                        text(typeof i === 'string' ? i : (i.content ?? ''))
                    )
                    .join('; ')
            )
        }
    }
    flush()
    return items.filter(i => i.question.length > 3 && i.answer.length > 10)
}
